import React, { useState, useEffect, useMemo } from 'react';
import {
  FaExchangeAlt,
  FaChevronDown,
  FaChevronUp,
  FaSyncAlt,
  FaInfoCircle
} from 'react-icons/fa';
import { getCurrencyRates } from '../api/client';

// Country detection patterns - matches backend currency_service.py
const COUNTRY_PATTERNS = {
  'US': [
    /\b(usa|u\.s\.a\.?|united states|america)\b/i,
    /,\s*(AL|AK|AZ|AR|CA|CO|CT|DE|FL|GA|HI|ID|IL|IN|IA|KS|KY|LA|ME|MD|MA|MI|MN|MS|MO|MT|NE|NV|NH|NJ|NM|NY|NC|ND|OH|OK|OR|PA|RI|SC|SD|TN|TX|UT|VT|VA|WA|WV|WI|WY|DC)\b/i,
  ],
  'CA': [/\bcanada\b/i, /,\s*(AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT)\b/i],
  'MX': [/\bmexico\b/i],
  'GB': [/\b(uk|united kingdom|england|scotland|wales|britain)\b/i],
  'FR': [/\bfrance\b/i],
  'DE': [/\bgermany\b/i],
  'IT': [/\bitaly\b/i],
  'ES': [/\bspain\b/i],
  'JP': [/\bjapan\b/i],
  'CN': [/\bchina\b/i],
  'AU': [/\baustralia\b/i],
  'NZ': [/\bnew zealand\b/i],
  'IN': [/\bindia\b/i],
  'BR': [/\bbrazil\b/i],
  'ZA': [/\bsouth africa\b/i],
  'UG': [/\buganda\b/i],
  'KE': [/\bkenya\b/i],
  'TZ': [/\btanzania\b/i],
  'NG': [/\bnigeria\b/i],
  'GH': [/\bghana\b/i],
  'EG': [/\begypt\b/i],
  'MA': [/\bmorocco\b/i],
  'TH': [/\bthailand\b/i],
  'VN': [/\bvietnam\b/i],
  'PH': [/\bphilippines\b/i],
  'ID': [/\b(indonesia|bali)\b/i],
  'MY': [/\bmalaysia\b/i],
  'SG': [/\bsingapore\b/i],
  'KR': [/\b(south korea|korea)\b/i],
  'AE': [/\b(uae|dubai|abu dhabi|united arab emirates)\b/i],
  'SA': [/\bsaudi arabia\b/i],
  'IL': [/\bisrael\b/i],
  'TR': [/\bturkey\b/i],
  'RU': [/\brussia\b/i],
  'PL': [/\bpoland\b/i],
  'NL': [/\b(netherlands|holland)\b/i],
  'BE': [/\bbelgium\b/i],
  'CH': [/\bswitzerland\b/i],
  'AT': [/\baustria\b/i],
  'PT': [/\bportugal\b/i],
  'GR': [/\bgreece\b/i],
  'IE': [/\bireland\b/i],
  'SE': [/\bsweden\b/i],
  'NO': [/\bnorway\b/i],
  'DK': [/\bdenmark\b/i],
  'FI': [/\bfinland\b/i],
  'CZ': [/\b(czech republic|czechia)\b/i],
  'HU': [/\bhungary\b/i],
  'RO': [/\bromania\b/i],
  'AR': [/\bargentina\b/i],
  'CL': [/\bchile\b/i],
  'CO': [/\bcolombia\b/i],
  'PE': [/\bperu\b/i],
  'HK': [/\bhong kong\b/i],
  'TW': [/\btaiwan\b/i],
  'RW': [/\brwanda\b/i],
  'ET': [/\bethiopia\b/i],
};

// Detect country code from location string
const detectCountry = (location) => {
  if (!location) return null;
  for (const [countryCode, patterns] of Object.entries(COUNTRY_PATTERNS)) {
    for (const pattern of patterns) {
      if (pattern.test(location)) {
        return countryCode;
      }
    }
  }
  return null;
};

const CurrencyCard = ({ requestId, startLocation, endLocation }) => {
  const [currencyData, setCurrencyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [customAmount, setCustomAmount] = useState('');

  // Determine if this is an international trip (different countries)
  const isInternationalTrip = useMemo(() => {
    const startCountry = detectCountry(startLocation);
    const endCountry = detectCountry(endLocation);

    // If we can't detect either country, assume international to be safe
    if (!startCountry || !endCountry) {
      // But if end location looks like it has a US state, it's probably domestic
      if (endCountry === 'US' || (!endCountry && /,\s*[A-Z]{2}\s*$/.test(endLocation))) {
        return false;
      }
      return endCountry !== null; // Only international if we detected a foreign country
    }

    return startCountry !== endCountry;
  }, [startLocation, endLocation]);

  useEffect(() => {
    // Only fetch currency data for international trips
    if (isInternationalTrip) {
      fetchCurrencyRates();
    }
  }, [requestId, baseCurrency, isInternationalTrip]);

  const fetchCurrencyRates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCurrencyRates(requestId, baseCurrency);
      setCurrencyData(data);
    } catch (err) {
      console.error('Error fetching currency rates:', err);
      setError('Unable to load exchange rates');
    } finally {
      setLoading(false);
    }
  };

  // Don't render for domestic trips
  if (!isInternationalTrip) {
    return null;
  }

  // Don't render if no destination currency detected
  if (!loading && currencyData && !currencyData.destination_currency) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-card p-4 mb-4">
        <div className="flex items-center gap-2 text-neutral-500">
          <FaSyncAlt className="animate-spin" />
          <span>Loading exchange rates...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-card p-4 mb-4">
        <div className="flex items-center gap-2 text-red-500">
          <FaInfoCircle />
          <span>{error}</span>
          <button
            onClick={fetchCurrencyRates}
            className="ml-auto text-primary-500 hover:text-primary-600 text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!currencyData) return null;

  const { destination_currency, destination_info, base, base_info, common_amounts, rates, last_updated } = currencyData;

  // Calculate custom amount conversion
  const customConverted = customAmount && destination_info
    ? (parseFloat(customAmount) * destination_info.rate).toFixed(2)
    : null;

  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-card overflow-hidden mb-4 border border-green-200">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-green-100/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white">
            <FaExchangeAlt />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-neutral-800">Currency Reference</h3>
            {destination_info && (
              <p className="text-sm text-neutral-600">
                {destination_info.flag} 1 {base} = {destination_info.symbol}{destination_info.rate.toFixed(2)} {destination_currency}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-neutral-500">
          <span className="text-xs">{isExpanded ? 'Hide' : 'Show'}</span>
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4">
          {/* Quick Converter */}
          <div className="bg-white rounded-lg p-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Quick Convert
            </label>
            <div className="flex gap-2 items-center">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">
                  {base_info.symbol}
                </span>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full pl-8 pr-3 py-2 border-2 border-neutral-300 rounded-lg focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none"
                />
              </div>
              <FaExchangeAlt className="text-green-500" />
              <div className="flex-1 bg-green-100 rounded-lg py-2 px-3 text-center">
                {customConverted ? (
                  <span className="font-bold text-green-700">
                    {destination_info.symbol}{customConverted} {destination_currency}
                  </span>
                ) : (
                  <span className="text-neutral-400">—</span>
                )}
              </div>
            </div>
          </div>

          {/* Common Amounts */}
          {common_amounts && common_amounts.length > 0 && (
            <div className="bg-white rounded-lg p-4">
              <h4 className="text-sm font-medium text-neutral-700 mb-3">Common Amounts</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {common_amounts.slice(0, 8).map((item) => (
                  <div
                    key={item.base_amount}
                    className="bg-neutral-50 rounded-lg p-2 text-center"
                  >
                    <div className="text-sm text-neutral-500">
                      {base_info.symbol}{item.base_amount}
                    </div>
                    <div className="font-semibold text-neutral-800">
                      {destination_info.symbol}{item.converted_amount.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Other Currencies */}
          {rates && Object.keys(rates).length > 1 && (
            <div className="bg-white rounded-lg p-4">
              <h4 className="text-sm font-medium text-neutral-700 mb-3">Other Currencies</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(rates)
                  .filter(([code]) => code !== destination_currency)
                  .slice(0, 6)
                  .map(([code, info]) => (
                    <div
                      key={code}
                      className="flex items-center gap-2 bg-neutral-50 rounded-lg p-2"
                    >
                      <span className="text-lg">{info.flag}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-neutral-500 truncate">{info.name}</div>
                        <div className="text-sm font-medium text-neutral-800">
                          {info.symbol}{info.rate.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-neutral-400">
            <span>Rates from {currencyData.source}</span>
            <span>Updated: {last_updated}</span>
          </div>

          {/* Base Currency Selector */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-neutral-600">Your currency:</span>
            <select
              value={baseCurrency}
              onChange={(e) => setBaseCurrency(e.target.value)}
              className="border border-neutral-300 rounded px-2 py-1 text-sm focus:border-green-500 outline-none"
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CAD">CAD (C$)</option>
              <option value="AUD">AUD (A$)</option>
              <option value="JPY">JPY (¥)</option>
              <option value="CHF">CHF (Fr)</option>
            </select>
            <button
              onClick={fetchCurrencyRates}
              className="ml-auto text-green-500 hover:text-green-600 flex items-center gap-1"
            >
              <FaSyncAlt className="text-xs" />
              Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencyCard;
